import * as XLSX from 'xlsx';
import { normalizeSupplierTicker } from './supplierTickerUtils';

/** @typedef {'ticker'|'name'|'description'|'industry'|'tier'|'country'|'sector'|'website'|'marketCap'|'employeeCount'|'keywords'|'supplyChainRisks'|'riskCategories'} BatchImportFieldId */

export const BATCH_IMPORT_FIELD_IDS = [
  'ticker',
  'name',
  'description',
  'industry',
  'tier',
  'legalName',
  'dunsNumber',
  'region',
  'categories',
  'annualSpend',
  'criticalityScore',
  'isSoleSource',
  'isStrategic',
  'uflpaFlag',
  'country',
  'sector',
  'website',
  'marketCap',
  'employeeCount',
  'keywords',
  'supplyChainRisks',
  'riskCategories',
];

const REQUIRED_FIELDS = ['ticker', 'name'];

/** @type {Record<string, string[]>} */
const ALIASES_BY_FIELD = {
  ticker: ['ticker', 'id', 'symbol', 'stock', 'code', 'ticker symbol', 'stock symbol', 'supplier code', 'sku'],
  name: ['name', 'company', 'company name', 'supplier', 'supplier name', 'title', 'legal name', 'organization'],
  description: ['description', 'desc', 'about', 'notes', 'summary'],
  industry: ['industry', 'vertical', 'business'],
  tier: ['tier', 'supplier tier', 'tier level', 'supplier_tier'],
  legalName: ['legal name', 'legal_name', 'legalname'],
  dunsNumber: ['duns', 'duns number', 'duns_number'],
  region: ['region', 'state region'],
  categories: ['categories', 'category list'],
  annualSpend: ['annual spend', 'annual_spend', 'spend'],
  criticalityScore: ['criticality', 'criticality score', 'criticality_score'],
  isSoleSource: ['sole source', 'is_sole_source', 'sole_source'],
  isStrategic: ['strategic', 'is_strategic', 'strategic supplier'],
  uflpaFlag: ['uflpa', 'uflpa flag', 'uflpa_flag'],
  country: ['country', 'nation', 'location country'],
  sector: ['sector', 'segment'],
  website: ['website', 'url', 'web', 'homepage'],
  marketCap: ['market cap', 'marketcap', 'market capitalization', 'mcap', 'capitalization'],
  employeeCount: ['employee count', 'employees', 'headcount', 'staff', 'fte'],
  keywords: ['keywords', 'tags', 'labels'],
  supplyChainRisks: ['supply chain risks', 'supply risks', 'sc risks'],
  riskCategories: ['risk categories', 'risk category', 'risks'],
};

function normalizeHeader(s) {
  return String(s ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function dedupeHeaders(rawHeaders) {
  const counts = {};
  return rawHeaders.map((h, i) => {
    const trimmed = String(h ?? '').trim();
    const base = trimmed || `Column_${i + 1}`;
    counts[base] = (counts[base] || 0) + 1;
    return counts[base] === 1 ? base : `${base} (${counts[base]})`;
  });
}

/**
 * @param {unknown[][]} matrix
 * @returns {{ headers: string[], rows: Record<string, unknown>[] }}
 */
function parseWithHeaderRow(matrix) {
  if (!matrix.length) {
    return { headers: [], rows: [] };
  }
  const headerRow = matrix[0].map((c) => (c == null ? '' : String(c)));
  const headers = dedupeHeaders(headerRow);
  const rows = [];
  for (let r = 1; r < matrix.length; r += 1) {
    const cells = matrix[r] || [];
    const row = {};
    let any = false;
    headers.forEach((key, col) => {
      const v = cells[col];
      const str = v == null ? '' : typeof v === 'string' ? v : String(v);
      row[key] = str;
      if (String(str).trim()) any = true;
    });
    if (any) rows.push(row);
  }
  return { headers, rows };
}

/**
 * NatureCrunch matrix sheets: attributes in column A, facility values in columns C+.
 * @returns {{ idRowIdx: number, dataColStart: number } | null}
 */
export function detectNatureCrunchColumnLayout(matrix) {
  if (!matrix.length) return null;

  for (let r = 0; r < Math.min(matrix.length, 15); r += 1) {
    const c0 = normalizeHeader(matrix[r]?.[0]);
    const c1 = normalizeHeader(matrix[r]?.[1]);
    if (c0 === 'attribute' && c1 === 'category') {
      const idRowIdx = matrix.findIndex((row, idx) => idx > r && normalizeHeader(row?.[0]) === 'id');
      if (idRowIdx >= 0) {
        return { idRowIdx, dataColStart: 2 };
      }
    }
  }

  const firstCell = String(matrix[0]?.[0] ?? '').toLowerCase();
  if (firstCell.includes('node:')) {
    const idRowIdx = matrix.findIndex((row) => normalizeHeader(row?.[0]) === 'id');
    if (idRowIdx >= 0) {
      const prevRowCategory = normalizeHeader(matrix[idRowIdx - 1]?.[1]) === 'category';
      return { idRowIdx, dataColStart: prevRowCategory ? 2 : 1 };
    }
  }

  return null;
}

/**
 * @param {unknown[][]} matrix
 * @param {{ idRowIdx: number, dataColStart: number }} layout
 * @returns {{ headers: string[], rows: Record<string, unknown>[] }}
 */
function parseNatureCrunchColumnMatrix(matrix, layout) {
  const { idRowIdx, dataColStart } = layout;
  const rawHeaders = [];
  for (let r = idRowIdx; r < matrix.length; r += 1) {
    const label = String(matrix[r]?.[0] ?? '').trim();
    if (!label) continue;
    if (label.toLowerCase().startsWith('node:')) continue;
    rawHeaders.push(label);
  }
  const headers = dedupeHeaders(rawHeaders);
  if (!headers.length) {
    return { headers: [], rows: [] };
  }

  const maxCols = matrix.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
  const rows = [];
  for (let col = dataColStart; col < maxCols; col += 1) {
    const row = {};
    let any = false;
    headers.forEach((header, headerIndex) => {
      const matrixRow = idRowIdx + headerIndex;
      const value = matrix[matrixRow]?.[col];
      const str = value == null ? '' : typeof value === 'string' ? value : String(value);
      row[header] = str;
      if (str.trim()) any = true;
    });
    if (any) rows.push(row);
  }
  return { headers, rows };
}

/**
 * @param {unknown[][]} matrix
 * @returns {'row'|'column'}
 */
export function isSupplierFacilitiesMatrixFile(matrix) {
  return Boolean(detectNatureCrunchColumnLayout(matrix));
}

/** NatureCrunch column-matrix layout (Suppliers and Supplier Facilities sheets). */
export const isNatureCrunchMatrixFile = isSupplierFacilitiesMatrixFile;

export function detectSpreadsheetOrientation(matrix) {
  if (detectNatureCrunchColumnLayout(matrix)) {
    return 'column';
  }

  const firstRow = matrix[0] || [];
  if (normalizeHeader(firstRow[0]) === 'attribute' && normalizeHeader(firstRow[1]) === 'category') {
    return 'column';
  }

  const firstRowNorm = firstRow.map((cell) => normalizeHeader(cell));
  if (firstRowNorm.includes('supplierid') || firstRowNorm.includes('ticker')) {
    return 'row';
  }

  const col0Norm = matrix.slice(0, 30).map((row) => normalizeHeader(row?.[0]));
  if (col0Norm.includes('supplierid') && !firstRowNorm.includes('supplierid')) {
    return 'column';
  }

  return 'row';
}

/**
 * @param {unknown[][]} matrix
 * @returns {{ headers: string[], rows: Record<string, unknown>[] }}
 */
function parseWithHeaderColumn(matrix) {
  if (!matrix.length) {
    return { headers: [], rows: [] };
  }

  const natureCrunchLayout = detectNatureCrunchColumnLayout(matrix);
  if (natureCrunchLayout) {
    return parseNatureCrunchColumnMatrix(matrix, natureCrunchLayout);
  }

  const rawHeaders = matrix.map((row) => (row?.[0] == null ? '' : String(row[0]))).filter((v) => v.trim());
  const headers = dedupeHeaders(rawHeaders);
  if (!headers.length) {
    return { headers: [], rows: [] };
  }

  const maxCols = matrix.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
  const rows = [];
  for (let col = 1; col < maxCols; col += 1) {
    const row = {};
    let any = false;
    headers.forEach((header, rowIndex) => {
      const value = matrix[rowIndex]?.[col];
      const str = value == null ? '' : typeof value === 'string' ? value : String(value);
      row[header] = str;
      if (str.trim()) any = true;
    });
    if (any) rows.push(row);
  }
  return { headers, rows };
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {unknown[][]}
 */
export function readSpreadsheetMatrix(buffer) {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error('EMPTY_WORKBOOK');
  }
  const sheet = workbook.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false,
  });
}

/**
 * @param {ArrayBuffer} buffer
 * @param {'row'|'column'} [orientation='row']
 * @returns {{ headers: string[], rows: Record<string, unknown>[] }}
 */
export function parseSpreadsheetBuffer(buffer, orientation = 'row') {
  const matrix = readSpreadsheetMatrix(buffer);
  if (!matrix.length) {
    return { headers: [], rows: [] };
  }
  if (orientation === 'column') {
    return parseWithHeaderColumn(matrix);
  }
  return parseWithHeaderRow(matrix);
}

/**
 * @param {File} file
 */
export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * @param {string[]} headers
 * @returns {Record<string, string|null>}
 */
export function guessColumnMapping(headers) {
  /** @type {Record<string, string|null>} */
  const mapping = {};
  BATCH_IMPORT_FIELD_IDS.forEach((id) => {
    mapping[id] = null;
  });
  const normToHeader = new Map();
  headers.forEach((h) => {
    normToHeader.set(normalizeHeader(h), h);
  });
  for (const fieldId of BATCH_IMPORT_FIELD_IDS) {
    const aliases = ALIASES_BY_FIELD[fieldId];
    if (!aliases) continue;
    for (const alias of aliases) {
      const match = normToHeader.get(normalizeHeader(alias));
      if (match) {
        mapping[fieldId] = match;
        break;
      }
    }
  }
  return mapping;
}

function splitList(value) {
  if (value == null || value === '') return [];
  const s = String(value).trim();
  if (!s) return [];
  return s
    .split(/[,;|\n\r]+/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function parseNumber(value) {
  if (value == null || value === '') return undefined;
  const s = String(value).replace(/[,\s]/g, '').trim();
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function parseIntField(value) {
  const n = parseNumber(value);
  if (n === undefined) return undefined;
  return Math.round(n);
}

function parseTier(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return undefined;
  if (raw === '1' || raw === '2' || raw === '3') return raw;

  const numeric = Number(raw);
  if (numeric === 1 || numeric === 2 || numeric === 3) {
    return String(numeric);
  }
  return undefined;
}

/**
 * @param {Record<string, unknown>} row
 * @param {Record<string, string|null>} columnMapping
 * @returns {Record<string, unknown>|null}
 */
export function rowToBatchCompany(row, columnMapping) {
  const get = (fieldId) => {
    const col = columnMapping[fieldId];
    if (!col) return '';
    const v = row[col];
    return v == null ? '' : String(v).trim();
  };

  let tickerRaw = get('ticker');
  if (!tickerRaw && row.id != null) {
    tickerRaw = String(row.id).trim();
  }
  const ticker = normalizeSupplierTicker(tickerRaw);
  const name = get('name');
  if (!ticker || !name) return null;

  /** @type {Record<string, unknown>} */
  const out = {
    ticker,
    name,
  };

  const desc = get('description');
  if (desc) out.description = desc;
  const industry = get('industry');
  if (industry) out.industry = industry;
  const tier = parseTier(get('tier'));
  if (tier) out.tier = tier;
  const legalName = get('legalName');
  if (legalName) out.legal_name = legalName;
  const dunsNumber = get('dunsNumber');
  if (dunsNumber) out.duns_number = dunsNumber;
  const region = get('region');
  if (region) out.region = region;
  const categories = splitList(columnMapping.categories ? row[columnMapping.categories] : '');
  if (categories.length) out.categories = categories;
  const annualSpend = get('annualSpend');
  if (annualSpend) out.annual_spend = annualSpend;
  const criticality = parseNumber(row[columnMapping.criticalityScore]);
  if (criticality !== undefined) out.criticality_score = criticality;
  const soleSource = get('isSoleSource');
  if (soleSource) out.is_sole_source = /^(true|1|yes)$/i.test(soleSource);
  const strategic = get('isStrategic');
  if (strategic) out.is_strategic = /^(true|1|yes)$/i.test(strategic);
  const uflpa = get('uflpaFlag');
  if (uflpa) out.uflpa_flag = /^(true|1|yes)$/i.test(uflpa);
  const country = get('country');
  if (country) out.country = country;
  const sector = get('sector');
  if (sector) out.sector = sector;
  const website = get('website');
  if (website) out.website = website;

  const mc = parseNumber(row[columnMapping.marketCap]);
  if (mc !== undefined) out.marketCap = mc;
  const ec = parseIntField(row[columnMapping.employeeCount]);
  if (ec !== undefined) out.employeeCount = ec;

  const kw = splitList(columnMapping.keywords ? row[columnMapping.keywords] : '');
  if (kw.length) out.keywords = kw;
  const scr = splitList(columnMapping.supplyChainRisks ? row[columnMapping.supplyChainRisks] : '');
  if (scr.length) out.supplyChainRisks = scr;
  const rc = splitList(columnMapping.riskCategories ? row[columnMapping.riskCategories] : '');
  if (rc.length) out.riskCategories = rc;

  return out;
}

/**
 * @param {Record<string, unknown>[]} rows
 * @param {Record<string, string|null>} columnMapping
 */
export function buildBatchPayload(rows, columnMapping) {
  const list = [];
  for (const row of rows) {
    const obj = rowToBatchCompany(row, columnMapping);
    if (obj) list.push(obj);
  }
  return list;
}

export function isMappingValid(columnMapping) {
  return REQUIRED_FIELDS.every((f) => Boolean(columnMapping[f]));
}

export const BATCH_CHUNK_SIZE = 200;
